import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Check, Users, ChevronDown, ChevronUp, FileDown, Shield, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { SortableHeader, sortData } from "../components/SortableHeader";
import ImportButton from "../components/ImportButton";
import { useLand } from "../hooks/useLand";
import { useStandort } from "../hooks/useStandort";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONEN = [
  "Kuechenchef",
  "Koch",
  "Sous Chef",
  "Serviceleiter",
  "Kellner",
  "Barkeeper",
  "Spueler",
  "Aushilfe",
  "Manager"
];

const ANSTELLUNGSARTEN = [
  { value: "Vollzeit", label: "Vollzeit" },
  { value: "Teilzeit", label: "Teilzeit" },
  { value: "Minijob", label: "Minijob (520€)" },
  { value: "Praktikum", label: "Praktikum" }
];

const STEUERKLASSEN = [
  { value: 1, label: "I - Ledig, ohne Kinder" },
  { value: 2, label: "II - Alleinerziehend" },
  { value: 3, label: "III - Verheiratet, Alleinverdiener" },
  { value: 4, label: "IV - Verheiratet, beide berufstätig" },
  { value: 5, label: "V - Verheiratet, Ehepartner Stkl. III" },
  { value: 6, label: "VI - Nebenbeschäftigung" }
];

const BUNDESLAENDER = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen", "Hamburg",
  "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen", "Nordrhein-Westfalen",
  "Rheinland-Pfalz", "Saarland", "Sachsen", "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"
];

const ALLE_MODULE = [
  { key: "dashboard", label: "Dashboard", gruppe: "Allgemein" },
  { key: "aufgaben", label: "Aufgaben", gruppe: "Allgemein" },
  { key: "checklisten", label: "Checklisten/HACCP", gruppe: "Hygiene" },
  { key: "temperatur", label: "Temperaturkontrolle", gruppe: "Hygiene" },
  { key: "schichtplan", label: "Schichtplan", gruppe: "Betrieb" },
  { key: "lager", label: "Lagerverwaltung", gruppe: "Betrieb" },
  { key: "bestellungen", label: "Bestellungen", gruppe: "Betrieb" },
  { key: "reservierungen", label: "Reservierungen", gruppe: "Service" },
  { key: "getraenke", label: "Getraenke", gruppe: "Karte" },
  { key: "speisen", label: "Speisen", gruppe: "Karte" },
  { key: "personal", label: "Personalverwaltung", gruppe: "Admin" },
  { key: "statistik", label: "Statistik", gruppe: "Admin" },
  { key: "einnahmen", label: "Einnahmen", gruppe: "Admin" },
  { key: "einstellungen", label: "Einstellungen", gruppe: "Admin" },
];

const ROLLEN_PRESETS = {
  "Koch": ["dashboard", "aufgaben", "checklisten", "temperatur", "lager", "bestellungen", "speisen"],
  "Kuechenchef": ["dashboard", "aufgaben", "checklisten", "temperatur", "schichtplan", "lager", "bestellungen", "speisen", "statistik"],
  "Kellner": ["dashboard", "aufgaben", "schichtplan", "reservierungen"],
  "Serviceleiter": ["dashboard", "aufgaben", "checklisten", "schichtplan", "reservierungen", "statistik", "einnahmen"],
  "Barkeeper": ["dashboard", "aufgaben", "schichtplan", "lager", "getraenke"],
  "Spueler": ["dashboard", "aufgaben", "checklisten"],
  "Aushilfe": ["dashboard", "aufgaben"],
  "Sous Chef": ["dashboard", "aufgaben", "checklisten", "temperatur", "lager", "bestellungen", "speisen"],
  "Manager": ["dashboard", "aufgaben", "checklisten", "temperatur", "schichtplan", "lager", "bestellungen", "reservierungen", "getraenke", "speisen", "personal", "statistik", "einnahmen", "einstellungen"],
};

export const Mitarbeiter = () => {
  const { land: aktuellesLand } = useLand();
  const { activeStandortId } = useStandort();
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMitarbeiter, setEditingMitarbeiter] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [formData, setFormData] = useState({
    name: "",
    position: "Kellner",
    stundenlohn: "",
    wochenstunden: "",
    anstellungsart: "Vollzeit",
    steuerklasse: 1,
    kirchensteuer: false,
    kinderfreibetraege: 0,
    bundesland: "Bayern",
    krankenversicherung_beitrag: 7.3,
    rentenversicherung_beitrag: 9.3,
    arbeitslosenversicherung_beitrag: 1.3,
    pflegeversicherung_beitrag: 1.7,
    zusatzbeitrag_kv: 0.8,
    // AT-spezifisch
    at_kinder_unter_18: 0,
    at_kinder_ab_18: 0,
    at_avab: false,
    at_aeab: false,
    at_avab_aeab_kinder: 0,
    at_pendlerpauschale_jahr: 0,
    at_pendlereuro_km: 0,
    zugriff_module: ["dashboard", "aufgaben"]
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStandortId]);

  const fetchData = async () => {
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const response = await axios.get(`${API}/mitarbeiter`, { params, withCredentials: true });
      setMitarbeiter(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Mitarbeiter");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      position: "Kellner",
      stundenlohn: "",
      wochenstunden: "",
      anstellungsart: "Vollzeit",
      steuerklasse: 1,
      kirchensteuer: false,
      kinderfreibetraege: 0,
      bundesland: "Bayern",
      krankenversicherung_beitrag: 7.3,
      rentenversicherung_beitrag: 9.3,
      arbeitslosenversicherung_beitrag: 1.3,
      pflegeversicherung_beitrag: 1.7,
      zusatzbeitrag_kv: 0.8,
      at_kinder_unter_18: 0,
      at_kinder_ab_18: 0,
      at_avab: false,
      at_aeab: false,
      at_avab_aeab_kinder: 0,
      at_pendlerpauschale_jahr: 0,
      at_pendlereuro_km: 0,
      zugriff_module: ["dashboard", "aufgaben"]
    });
    setEditingMitarbeiter(null);
    setShowAdvanced(false);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (ma) => {
    setEditingMitarbeiter(ma);
    setFormData({
      name: ma.name,
      position: ma.position,
      stundenlohn: ma.stundenlohn.toString(),
      wochenstunden: ma.wochenstunden.toString(),
      anstellungsart: ma.anstellungsart,
      steuerklasse: ma.steuerklasse || 1,
      kirchensteuer: ma.kirchensteuer || false,
      kinderfreibetraege: ma.kinderfreibetraege || 0,
      bundesland: ma.bundesland || "Bayern",
      krankenversicherung_beitrag: ma.krankenversicherung_beitrag || 7.3,
      rentenversicherung_beitrag: ma.rentenversicherung_beitrag || 9.3,
      arbeitslosenversicherung_beitrag: ma.arbeitslosenversicherung_beitrag || 1.3,
      pflegeversicherung_beitrag: ma.pflegeversicherung_beitrag || 1.7,
      zusatzbeitrag_kv: ma.zusatzbeitrag_kv || 0.8,
      at_kinder_unter_18: ma.at_kinder_unter_18 || 0,
      at_kinder_ab_18: ma.at_kinder_ab_18 || 0,
      at_avab: ma.at_avab || false,
      at_aeab: ma.at_aeab || false,
      at_avab_aeab_kinder: ma.at_avab_aeab_kinder || 0,
      at_pendlerpauschale_jahr: ma.at_pendlerpauschale_jahr || 0,
      at_pendlereuro_km: ma.at_pendlereuro_km || 0,
      zugriff_module: ma.zugriff_module || ROLLEN_PRESETS[ma.position] || ["dashboard", "aufgaben"]
    });
    setDialogOpen(true);
  };

  const calculatePreview = () => {
    const stundenlohn = parseFloat(formData.stundenlohn) || 0;
    const wochenstunden = parseFloat(formData.wochenstunden) || 0;
    const brutto = stundenlohn * wochenstunden * 4.33;
    
    const svAN = brutto * (
      formData.krankenversicherung_beitrag +
      formData.rentenversicherung_beitrag +
      formData.arbeitslosenversicherung_beitrag +
      formData.pflegeversicherung_beitrag +
      formData.zusatzbeitrag_kv
    ) / 100;
    
    const svAG = brutto * 0.21; // Vereinfacht
    
    return {
      brutto: brutto.toFixed(2),
      svAN: svAN.toFixed(2),
      svAG: svAG.toFixed(2),
      gesamt: (brutto + svAG).toFixed(2)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.stundenlohn || !formData.wochenstunden) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }

    try {
      const payload = {
        ...formData,
        stundenlohn: parseFloat(formData.stundenlohn),
        wochenstunden: parseFloat(formData.wochenstunden),
        standort_id: formData.standort_id !== undefined
          ? formData.standort_id
          : (editingMitarbeiter ? editingMitarbeiter.standort_id : activeStandortId) || null,
      };

      if (editingMitarbeiter) {
        await axios.put(`${API}/mitarbeiter/${editingMitarbeiter.id}`, payload, { withCredentials: true });
        toast.success("Mitarbeiter aktualisiert");
      } else {
        await axios.post(`${API}/mitarbeiter`, payload, { withCredentials: true });
        toast.success("Mitarbeiter erstellt");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Mitarbeiter wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/mitarbeiter/${id}`, { withCredentials: true });
      toast.success("Mitarbeiter gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const downloadLohnabrechnung = async (ma) => {
    try {
      const response = await axios.get(`${API}/mitarbeiter/${ma.id}/lohnabrechnung`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // PDF herunterladen
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const monat = new Date().toLocaleString('de-DE', { month: 'long' });
      const jahr = new Date().getFullYear();
      link.setAttribute('download', `Lohnabrechnung_${ma.name.replace(/\s/g, '_')}_${monat}_${jahr}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Lohnabrechnung heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Erstellen der Lohnabrechnung");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const getTotalKosten = () => {
    return mitarbeiter.reduce((sum, m) => sum + (m.gesamt_kosten || 0), 0);
  };

  const preview = calculatePreview();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#858585]">Laden...</div>
      </div>
    );
  }

  return (
    <div data-testid="mitarbeiter-page">
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Mitarbeiter</h1>
            <p className="page-subtitle">Personalverwaltung mit detaillierter Kostenberechnung</p>
          </div>
          <Button 
            onClick={openCreateDialog}
            className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm uppercase text-xs tracking-wide btn-press"
            data-testid="add-mitarbeiter-btn"
          >
            <Plus size={16} className="mr-2" />
            Neuer Mitarbeiter
          </Button>
          <ImportButton kategorie="mitarbeiter" felder={["name","position","stundenlohn","wochenstunden","anstellungsart"]} onImportSuccess={fetchData} />
        </div>
      </header>

      {mitarbeiter.length > 0 && (
        <div className="px-8 py-4">
          <div className="bg-[#E8F5E9] border border-[#C8E6C9] p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-[#2E7D32] font-medium">Gesamte Personalkosten / Monat</p>
              <p className="font-mono text-2xl text-[#1B5E20] font-medium mt-1">
                {formatCurrency(getTotalKosten())}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-[#2E7D32]">{mitarbeiter.length} Mitarbeiter</p>
              <p className="text-xs text-[#558B2F]">inkl. AG-Anteile</p>
            </div>
          </div>
        </div>
      )}

      {mitarbeiter.length === 0 ? (
        <div className="empty-state">
          <Users size={64} className="mx-auto text-[#D1CFC9]" />
          <h3>Keine Mitarbeiter vorhanden</h3>
          <p>Erfassen Sie Ihre Mitarbeiter für die Personalkostenberechnung</p>
          <Button 
            onClick={openCreateDialog}
            className="mt-4 bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm"
          >
            <Plus size={16} className="mr-2" />
            Ersten Mitarbeiter erfassen
          </Button>
        </div>
      ) : (
        <div className="data-table-container" data-testid="mitarbeiter-table">
          <table className="data-table">
            <thead>
              <tr>
                <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Position" sortKey="position" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Anstellung" sortKey="anstellungsart" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Stundenlohn" sortKey="stundenlohn" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Std./Woche" sortKey="wochenstunden" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Brutto" sortKey="brutto_monat" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="SV (AN)" sortKey="sv_beitraege_an" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="AG-Kosten" sortKey="lohnnebenkosten" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Gesamt" sortKey="gesamt_kosten" sortConfig={sortConfig} onSort={setSortConfig} />
                <th className="text-center">Zugriff</th>
                <th className="text-center">Login</th>
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {sortData(mitarbeiter, sortConfig).map((ma) => (
                <tr key={ma.id} data-testid={`mitarbeiter-row-${ma.id}`}>
                  <td className="font-body font-medium">
                    {ma.name}
                    {ma.kirchensteuer && <span className="text-xs text-[#858585] ml-1">(KiSt)</span>}
                  </td>
                  <td>{ma.position}</td>
                  <td>
                    <span className="badge badge-neutral">{ma.anstellungsart}</span>
                  </td>
                  <td>{formatCurrency(ma.stundenlohn)}</td>
                  <td>{ma.wochenstunden}h</td>
                  <td>{formatCurrency(ma.brutto_monat)}</td>
                  <td className="text-[#858585]">{formatCurrency(ma.sv_beitraege_an)}</td>
                  <td className="text-[#858585]">{formatCurrency(ma.lohnnebenkosten)}</td>
                  <td className="font-medium">{formatCurrency(ma.gesamt_kosten)}</td>
                  <td className="text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
                      <Lock size={10} />
                      {ma.zugriff_module?.length || 0} Module
                    </span>
                  </td>
                  <td className="text-center">
                    {ma.pin ? (
                      <div className="text-xs">
                        <span className="font-mono bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">{ma.login_code}</span>
                        <span className="text-slate-400 mx-1">/</span>
                        <span className="font-mono bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">{ma.pin}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        className="btn-icon" 
                        onClick={() => downloadLohnabrechnung(ma)}
                        title="Lohnabrechnung herunterladen"
                        data-testid={`download-lohnabrechnung-${ma.id}`}
                      >
                        <FileDown size={16} />
                      </button>
                      <button className="btn-icon" onClick={() => openEditDialog(ma)} title="Bearbeiten">
                        <Edit2 size={16} />
                      </button>
                      <button className="btn-icon danger" onClick={() => handleDelete(ma.id)} title="Löschen">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingMitarbeiter ? "Mitarbeiter bearbeiten" : "Neuer Mitarbeiter"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="z.B. Max Mustermann"
                  className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39]"
                  data-testid="mitarbeiter-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Position</Label>
                <Select value={formData.position} onValueChange={(v) => {
                    const presetModule = ROLLEN_PRESETS[v] || ["dashboard", "aufgaben"];
                    setFormData({...formData, position: v, zugriff_module: presetModule});
                  }}>
                  <SelectTrigger className="border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POSITIONEN.map((pos) => (
                      <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Anstellungsart</Label>
                <Select value={formData.anstellungsart} onValueChange={(v) => setFormData({...formData, anstellungsart: v})}>
                  <SelectTrigger className="border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ANSTELLUNGSARTEN.map((art) => (
                      <SelectItem key={art.value} value={art.value}>{art.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Bundesland</Label>
                <Select value={formData.bundesland} onValueChange={(v) => setFormData({...formData, bundesland: v})}>
                  <SelectTrigger className="border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUNDESLAENDER.map((bl) => (
                      <SelectItem key={bl} value={bl}>{bl}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Stundenlohn (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.stundenlohn}
                  onChange={(e) => setFormData({...formData, stundenlohn: e.target.value})}
                  placeholder="z.B. 12.82 (Mindestlohn 2024)"
                  className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                  data-testid="mitarbeiter-stundenlohn-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Wochenstunden *</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.wochenstunden}
                  onChange={(e) => setFormData({...formData, wochenstunden: e.target.value})}
                  placeholder="z.B. 40"
                  className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                  data-testid="mitarbeiter-stunden-input"
                />
              </div>
            </div>

            {/* Zugriff & Berechtigungen */}
            <div className="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Shield size={16} className="text-violet-500" />
                <h4 className="text-sm font-semibold">Zugriff & Berechtigungen</h4>
                <span className="text-xs text-slate-400 ml-auto">{formData.zugriff_module.length} Module</span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Waehlen Sie, auf welche Funktionen dieser Mitarbeiter nach PIN-Login Zugriff hat.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ALLE_MODULE.map((modul) => {
                  const isChecked = formData.zugriff_module.includes(modul.key);
                  const isDashboard = modul.key === "dashboard";
                  return (
                    <label key={modul.key}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors text-sm
                        ${isChecked ? 'bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700' : 'bg-slate-50 dark:bg-slate-700/30 border border-transparent'}
                        ${isDashboard ? 'opacity-60' : ''}`}>
                      <input type="checkbox" checked={isChecked} disabled={isDashboard}
                        onChange={(e) => {
                          if (isDashboard) return;
                          const newModules = e.target.checked
                            ? [...formData.zugriff_module, modul.key]
                            : formData.zugriff_module.filter(m => m !== modul.key);
                          setFormData({...formData, zugriff_module: newModules});
                        }}
                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                        data-testid={`modul-${modul.key}`} />
                      <div>
                        <span className={isChecked ? 'text-violet-700 dark:text-violet-300 font-medium' : 'text-slate-600 dark:text-slate-400'}>
                          {modul.label}
                        </span>
                        <span className="text-[9px] text-slate-400 ml-1">{modul.gruppe}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-3">
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setFormData({...formData, zugriff_module: ALLE_MODULE.map(m => m.key)})}
                  className="text-xs">Alle auswaehlen</Button>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setFormData({...formData, zugriff_module: ["dashboard", "aufgaben"]})}
                  className="text-xs">Minimal</Button>
              </div>
            </div>

            {/* Steuer & Sozialversicherung */}
            <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
              <CollapsibleTrigger asChild>
                <Button type="button" variant="outline" className="w-full justify-between rounded-sm">
                  Steuer & Sozialversicherung
                  {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Steuerklasse</Label>
                    <Select value={formData.steuerklasse.toString()} onValueChange={(v) => setFormData({...formData, steuerklasse: parseInt(v)})}>
                      <SelectTrigger className="border-stone-300 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STEUERKLASSEN.map((sk) => (
                          <SelectItem key={sk.value} value={sk.value.toString()}>{sk.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Kinderfreibeträge</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.kinderfreibetraege}
                      onChange={(e) => setFormData({...formData, kinderfreibetraege: parseFloat(e.target.value) || 0})}
                      className="border-stone-300 rounded-sm font-mono"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.kirchensteuer}
                    onCheckedChange={(checked) => setFormData({...formData, kirchensteuer: checked})}
                  />
                  <Label>Kirchensteuerpflichtig (8-9%)</Label>
                </div>

                <div className="border-t border-stone-100 pt-4">
                  <p className="text-xs uppercase tracking-wide text-[#858585] mb-3">Sozialversicherungsbeiträge (AN-Anteil %)</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-[#5C5C5C]">Krankenvers.</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.krankenversicherung_beitrag}
                        onChange={(e) => setFormData({...formData, krankenversicherung_beitrag: parseFloat(e.target.value) || 0})}
                        className="border-stone-300 rounded-sm font-mono text-sm h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#5C5C5C]">Rentenvers.</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.rentenversicherung_beitrag}
                        onChange={(e) => setFormData({...formData, rentenversicherung_beitrag: parseFloat(e.target.value) || 0})}
                        className="border-stone-300 rounded-sm font-mono text-sm h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#5C5C5C]">Arbeitslosenvers.</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.arbeitslosenversicherung_beitrag}
                        onChange={(e) => setFormData({...formData, arbeitslosenversicherung_beitrag: parseFloat(e.target.value) || 0})}
                        className="border-stone-300 rounded-sm font-mono text-sm h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#5C5C5C]">Pflegevers.</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.pflegeversicherung_beitrag}
                        onChange={(e) => setFormData({...formData, pflegeversicherung_beitrag: parseFloat(e.target.value) || 0})}
                        className="border-stone-300 rounded-sm font-mono text-sm h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#5C5C5C]">Zusatzbeitrag KV</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.zusatzbeitrag_kv}
                        onChange={(e) => setFormData({...formData, zusatzbeitrag_kv: parseFloat(e.target.value) || 0})}
                        className="border-stone-300 rounded-sm font-mono text-sm h-8"
                      />
                    </div>
                  </div>
                </div>

                {aktuellesLand === "AT" && (
                  <div className="border-t border-stone-100 pt-4 space-y-3" data-testid="at-tarif-section">
                    <p className="text-xs uppercase tracking-wide text-[#858585]">Österreich: Absetzbeträge & Pendlerregelung (Jahresbasis)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-[#5C5C5C]">Kinder &lt; 18 (Familienbonus 2.000 €/Jahr)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.at_kinder_unter_18}
                          onChange={(e) => setFormData({...formData, at_kinder_unter_18: parseInt(e.target.value) || 0})}
                          className="border-stone-300 rounded-sm font-mono text-sm h-8"
                          data-testid="at-kinder-unter18"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-[#5C5C5C]">Kinder ≥ 18 (Familienbonus 700 €/Jahr)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.at_kinder_ab_18}
                          onChange={(e) => setFormData({...formData, at_kinder_ab_18: parseInt(e.target.value) || 0})}
                          className="border-stone-300 rounded-sm font-mono text-sm h-8"
                          data-testid="at-kinder-ab18"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={formData.at_avab}
                          onCheckedChange={(c) => setFormData({...formData, at_avab: c, at_aeab: c ? false : formData.at_aeab})}
                          data-testid="at-avab-toggle"
                        />
                        <Label className="text-xs">AVAB (Alleinverdiener)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={formData.at_aeab}
                          onCheckedChange={(c) => setFormData({...formData, at_aeab: c, at_avab: c ? false : formData.at_avab})}
                          data-testid="at-aeab-toggle"
                        />
                        <Label className="text-xs">AEAB (Alleinerzieher)</Label>
                      </div>
                    </div>
                    {(formData.at_avab || formData.at_aeab) && (
                      <div className="space-y-1">
                        <Label className="text-xs text-[#5C5C5C]">Kinder für AVAB/AEAB-Höhe</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.at_avab_aeab_kinder}
                          onChange={(e) => setFormData({...formData, at_avab_aeab_kinder: parseInt(e.target.value) || 0})}
                          className="border-stone-300 rounded-sm font-mono text-sm h-8 max-w-[200px]"
                          data-testid="at-avab-kinder"
                        />
                        <p className="text-xs text-slate-400">1 Kind: 601 €/Jahr • 2 Kinder: 813 €/Jahr • +268 €/Kind ab 3.</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-[#5C5C5C]">Pendlerpauschale (€/Jahr)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.at_pendlerpauschale_jahr}
                          onChange={(e) => setFormData({...formData, at_pendlerpauschale_jahr: parseFloat(e.target.value) || 0})}
                          className="border-stone-300 rounded-sm font-mono text-sm h-8"
                          data-testid="at-pendlerpauschale"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-[#5C5C5C]">Pendlereuro (km einfach)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          value={formData.at_pendlereuro_km}
                          onChange={(e) => setFormData({...formData, at_pendlereuro_km: parseFloat(e.target.value) || 0})}
                          className="border-stone-300 rounded-sm font-mono text-sm h-8"
                          data-testid="at-pendlereuro-km"
                        />
                        <p className="text-xs text-slate-400">2 €/km × 2 (hin & rück) Jahresbetrag</p>
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Summary */}
            <div className="receipt-summary">
              <div className="receipt-header">
                <p className="receipt-title">Kostenvorschau</p>
              </div>
              <div className="receipt-line">
                <span>Brutto / Monat:</span>
                <span>{formatCurrency(parseFloat(preview.brutto))}</span>
              </div>
              <div className="receipt-line text-[#858585]">
                <span>- SV Arbeitnehmer:</span>
                <span>{formatCurrency(parseFloat(preview.svAN))}</span>
              </div>
              <div className="receipt-line text-[#858585]">
                <span>+ AG-Lohnnebenkosten:</span>
                <span>{formatCurrency(parseFloat(preview.svAG))}</span>
              </div>
              <div className="receipt-divider" />
              <div className="receipt-total">
                <span>Gesamtkosten / Monat:</span>
                <span className="text-[#3A4D39]">{formatCurrency(parseFloat(preview.gesamt))}</span>
              </div>
              <p className="text-xs text-[#858585] mt-2 text-center">
                Berechnung: Stundenlohn × Wochenstunden × 4,33 Wochen + AG-Anteile
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm btn-press">
                <Check size={16} className="mr-2" />
                {editingMitarbeiter ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Mitarbeiter;
